import { COLOR } from '../constants/colors';
import {
    INVOICE_STATUS,
    INVOICES_STATUSES_DICTIONARY,
} from '../constants/invoices/statuses';
import Entry from '../db/services/Entry';
import { EntryDBServiceOptions } from '../types/dbService';
import { InvoiceFinancialDetails, InvoiceModel } from '../types/Invoice';

import { getLocal, isFuture } from './date';
import InvoiceInvoicingProduct from 'shared/db/services/InvoiceInvoicingProduct';
import InvoiceInvoicingProductModel from 'shared/db/models/InvoiceInvoicingProduct';
import { EntryProcedureModel } from 'shared/types/EntryProcedure';

const REFERENCE_PREFIX = 'EQ';
const REFERENCE_BASE36 = 36;
const REFERENCE_RANDOM_CHARS = 4;
const REFERENCE_TOTAL_COMBINATIONS = Math.pow(
    REFERENCE_BASE36,
    REFERENCE_RANDOM_CHARS,
);

/**
 * Generates a unique 12-character invoice payment reference.
 * Offline-safe, ~1.6M unique values/day, fits NZ bank 12-char limit
 *
 * Format: `EQ` + `YYMMDD` + 4 base36 chars (0–9, A–Z)
 */
export const generateReference = () => {
    // Using padded tokens (LL, dd) to ensure the date is always 6 chars (YYMMDD),
    // as single-digit days/months (e.g. June → 6) would produce fewer chars without padding
    const date = getLocal().toFormat('yyLLdd');

    const randomInt = Math.floor(Math.random() * REFERENCE_TOTAL_COMBINATIONS);

    // Convert to base36 (0-9, A-Z) and ensure it's always 4 chars with left-padding
    const randomPart = randomInt
        .toString(REFERENCE_BASE36)
        .toUpperCase()
        .padStart(REFERENCE_RANDOM_CHARS, '0');

    return REFERENCE_PREFIX + date + randomPart;
};

export const calculateInvoiceDetailsFromItems = ({
    taxRate,
    productsTaxRate,
    procedures,
    products,
}: {
    taxRate: number;
    productsTaxRate: number;
    procedures: { price: string; quantity: number }[];
    products?: Pick<InvoiceInvoicingProductModel, 'price' | 'quantity'>[];
}): InvoiceFinancialDetails => {
    const proceduresSum = procedures.reduce(
        (acc, curr) => acc + Number(curr.price) * Number(curr.quantity),
        0,
    );

    const proceduresTax = taxRate ? (proceduresSum * taxRate) / 100 : 0;

    const productsSum = products
        ? products.reduce(
              (acc, { price, quantity }) => acc + Number(price) * quantity,
              0,
          )
        : 0;

    const productsTax = productsTaxRate
        ? (productsSum * productsTaxRate) / 100
        : 0;

    const subtotalSum = proceduresSum + productsSum;
    const totalSum = proceduresSum + proceduresTax + productsSum + productsTax;

    return {
        subtotalSum,
        totalSum,
        proceduresSum,
        proceduresTax,
        productsSum,
        productsTax,
    };
};

export const calculateInvoiceDetails = async (
    options: EntryDBServiceOptions,
    invoice: {
        id: string;
        taxRate?: string;
        productsTaxRate?: string;
    },
): Promise<{ totalSum: number; subtotalSum: number }> => {
    const entryService = new Entry(options);
    const invoicingProductService = new InvoiceInvoicingProduct({
        database: options.database,
        logDBAction: options.logger.logDBAction,
    });

    const entries = await entryService.getByParam('invoice_id', invoice.id);
    const products = await invoicingProductService.getByParam(
        'invoice_id',
        invoice.id,
    );

    let procedures: EntryProcedureModel[] = [];

    for (let entry of entries) {
        const entryProcedures = await entry.entryProcedures.fetch();

        procedures = procedures.concat(entryProcedures);
    }

    const taxRate = Number(invoice.taxRate) || 0;
    const productsTaxRate = Number(invoice.productsTaxRate) || taxRate;

    const { subtotalSum, totalSum } = calculateInvoiceDetailsFromItems({
        taxRate,
        productsTaxRate,
        procedures,
        products,
    });

    return { subtotalSum, totalSum };
};

export const calculateInvoicesTotalSum = async ({
    options,
    invoices,
    invoiceSystemEnabled,
}: {
    options: EntryDBServiceOptions;
    invoices: {
        id: string;
        taxRate?: string;
        productsTaxRate?: string;
        applyTaxes?: boolean;
    }[];
    invoiceSystemEnabled?: boolean;
}): Promise<number> => {
    const results = await Promise.all(
        invoices.map(async (invoice) => {
            const { totalSum, subtotalSum } = await calculateInvoiceDetails(
                options,
                invoice,
            );

            return invoice.applyTaxes && invoiceSystemEnabled
                ? totalSum
                : subtotalSum;
        }),
    );

    return results.reduce((total, invoiceSum) => total + invoiceSum, 0);
};

export const getInvoiceStatus = ({
    hasAccountingProvider,
    sentToContact,
    sentToContactTime,
    status,
    isInternalProvider,
    translateFn,
}: {
    hasAccountingProvider: boolean;
    translateFn: (translationKey: string) => string;
    status: string;
    sentToContactTime: string;
    sentToContact: boolean;
    isInternalProvider?: boolean;
}): string => {
    try {
        if (!!hasAccountingProvider && status === INVOICE_STATUS.authorised) {
            if (sentToContactTime) {
                return translateFn(
                    INVOICES_STATUSES_DICTIONARY[
                        sentToContact
                            ? INVOICE_STATUS.sent
                            : INVOICE_STATUS.queued
                    ],
                );
            }
        }

        if (status === INVOICE_STATUS.authorised && isInternalProvider) {
            return translateFn(
                INVOICES_STATUSES_DICTIONARY[INVOICE_STATUS.approved],
            );
        }

        return translateFn(INVOICES_STATUSES_DICTIONARY[status.toUpperCase()]);
    } catch {
        return status;
    }
};

/** Whether an invoice can currently be edited. */
export function canEditInvoice({
    status,
    isInvoiceArchived,
    isProviderConnectionError,
    hasAccountingProvider,
}: {
    status?: string;
    isInvoiceArchived: boolean;
    isProviderConnectionError: boolean;
    hasAccountingProvider: boolean;
}): boolean {
    // Archived invoices are read-only copies of provider-side records.
    if (isInvoiceArchived) return false;

    // Can't safely edit while we can't confirm provider-side state.
    if (isProviderConnectionError) return false;

    // Drafts are local-only and always editable.
    if (status === INVOICE_STATUS.draft) return true;

    // Authorised invoices are editable only if not managed by an external accounting provider
    if (status === INVOICE_STATUS.authorised) return !hasAccountingProvider;

    return false;
}

export const getInvoiceStatusColor = (
    status: string,
    dueDateTime: string,
): { boldText: boolean; color: string } => {
    let parsedStatus = '';

    if (status && dueDateTime) {
        if (
            !isFuture(dueDateTime, -1) &&
            [INVOICE_STATUS.authorised, INVOICE_STATUS.sent].includes(
                status as INVOICE_STATUS,
            )
        ) {
            parsedStatus = INVOICE_STATUS.overdue;
        } else {
            parsedStatus = status;
        }
    }

    switch (parsedStatus) {
        case INVOICE_STATUS.paid:
            return {
                boldText: true,
                color: COLOR.green,
            };
        case INVOICE_STATUS.draft:
            return {
                boldText: false,
                color: COLOR.light_gray,
            };
        case INVOICE_STATUS.overdue:
            return {
                boldText: true,
                color: COLOR.flamingo,
            };
        default:
            return {
                boldText: false,
                color: COLOR.black,
            };
    }
};

export const getInvoiceBusinessOrContactName = (contact?: {
    firstName: string;
    lastName?: string | null;
    businessName?: string | null;
}) => {
    if (!contact) {
        return '-';
    }

    const contactName = `${contact.firstName} ${
        contact.lastName ? `${contact.lastName}` : ''
    }`;

    return contact.businessName
        ? `${contactName} (${contact.businessName})`
        : contactName;
};

/** Only the Draft status allows entries to be added/edited/removed on an invoice; every other status locks them. */
export function canEditInvoiceEntries(status?: string | null): boolean {
    return status === INVOICE_STATUS.draft;
}

export const getInvoiceFileName = (invoiceNumber: string) =>
    `INV-${invoiceNumber}`;

export const filterCurrentMonthInvoices = (
    invoices: InvoiceModel[],
): InvoiceModel[] => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();

    const startOfMonth = new Date(currentYear, currentMonth, 1);
    const endOfMonth = new Date(currentYear, currentMonth + 1, 1);

    return invoices.filter((invoice) => {
        const invoiceDate = new Date(invoice.createdAt);
        return invoiceDate >= startOfMonth && invoiceDate < endOfMonth;
    });
};
